// src/pages/ClassesPage.js - Full CRUD: Classes, Groups, Assignments
// FIXES: no duplicate assignments, deactivated classes visible, edit updates correctly
import React, { useState, useEffect } from "react";
import { classAPI, groupAPI } from "../api/api";
import toast from "react-hot-toast";
import "./CommonPage.css";

const KN_DIGITS = "೦೧೨೩೪೫೬೭೮೯";
const toEnglish = (str) =>
  String(str).replace(/[೦-೯]/g, (d) => KN_DIGITS.indexOf(d));
const toKannada = (n) =>
  String(n).replace(/[0-9]/g, (d) => KN_DIGITS[parseInt(d)]);

// ── Shared helpers ───────────────────────────────────────────
export function Modal({ title, onClose, children, wide }) {
  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box" style={wide ? { maxWidth: 560 } : {}}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
export function FormRow({ label, children, hint }) {
  return (
    <div className="form-row">
      <label className="form-row-label">
        {label}
        {hint && (
          <span
            style={{
              fontSize: "0.68rem",
              color: "rgba(255,255,255,0.3)",
              marginLeft: "0.4rem",
            }}
          >
            {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  );
}
export function ModalActions({
  loading,
  onSave,
  onCancel,
  saveLabel = "ಉಳಿಸಿ",
}) {
  return (
    <div className="modal-actions">
      <button className="modal-cancel-btn" onClick={onCancel}>
        ರದ್ದು
      </button>
      <button className="modal-save-btn" onClick={onSave} disabled={loading}>
        {loading ? (
          <>
            <span className="spinner-sm" />
            ಉಳಿಸುತ್ತಿದೆ...
          </>
        ) : (
          saveLabel
        )}
      </button>
    </div>
  );
}
export function ConfirmModal({ title, message, onConfirm, onCancel, loading }) {
  return (
    <Modal title={title} onClose={onCancel}>
      <p
        style={{
          fontFamily: "Noto Sans Kannada",
          fontSize: "0.88rem",
          color: "rgba(255,255,255,0.7)",
          marginBottom: "1.2rem",
          lineHeight: 1.6,
        }}
      >
        {message}
      </p>
      <div className="modal-actions">
        <button className="modal-cancel-btn" onClick={onCancel}>
          ರದ್ದು
        </button>
        <button
          className="modal-save-btn"
          style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)" }}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-sm" />
              ...
            </>
          ) : (
            "🗑️ ಅಳಿಸಿ"
          )}
        </button>
      </div>
    </Modal>
  );
}
export function NumInput({ value, onChange, placeholder, className }) {
  return (
    <input
      className={className || "modal-input"}
      type="text"
      inputMode="numeric"
      placeholder={placeholder || "0"}
      value={value}
      onChange={(e) => onChange(toEnglish(e.target.value))}
    />
  );
}

// ════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════
export default function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [clsForm, setClsForm] = useState({
    class_number: "",
    name_kannada: "",
    name_english: "",
    display_order: "",
  });
  const [grpForm, setGrpForm] = useState({
    name_kannada: "",
    name_english: "",
    description_kannada: "",
  });
  const [assignForm, setAssignForm] = useState({
    class_id: "",
    group_id: "",
    effective_from: new Date().toISOString().slice(0, 10),
    change_reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("classes");
  const [showInactive, setShowInactive] = useState(true); // always show inactive by default
  const [inlineReactivate, setInlineReactivate] = useState(null); // class to reactivate inline

  const load = () => {
    // Load ALL classes including inactive so deleted ones still appear
    classAPI
      .list({ active_only: false })
      .then((r) => setClasses(r.data.data || []))
      .catch(() => {});
    groupAPI
      .list()
      .then((r) => setGroups(r.data.data || []))
      .catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setInlineReactivate(null);
  };

  // Open assign modal pre-filled with class's CURRENT group
  const openAssign = (cls) => {
    setAssignForm({
      class_id: cls.id,
      group_id: cls.group_id || "", // pre-fill current group
      effective_from: new Date().toISOString().slice(0, 10),
      change_reason: "",
    });
    setSelected(cls);
    setModal("assign");
  };

  const openEditCls = (cls) => {
    setSelected(cls);
    setClsForm({
      class_number: String(cls.class_number),
      name_kannada: cls.name_kannada,
      name_english: cls.name_english,
      display_order: String(cls.display_order),
      is_active: cls.is_active,
    });
    setModal("editCls");
  };
  const openEditGrp = (grp) => {
    setSelected(grp);
    setGrpForm({
      name_kannada: grp.name_kannada,
      name_english: grp.name_english,
      description_kannada: grp.description_kannada || "",
    });
    setModal("editGrp");
  };

  // ── Save Class ──────────────────────────────────────────
  const saveClass = async () => {
    const num = parseInt(toEnglish(String(clsForm.class_number)));
    if (!num || !clsForm.name_kannada.trim()) {
      toast.error("ತರಗತಿ ಸಂಖ್ಯೆ ಮತ್ತು ಕನ್ನಡ ಹೆಸರು ಅಗತ್ಯ");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        class_number: num,
        name_kannada: clsForm.name_kannada.trim(),
        name_english: clsForm.name_english.trim() || `Class ${num}`,
        display_order:
          parseInt(toEnglish(String(clsForm.display_order))) || num,
      };
      if (modal === "editCls") {
        await classAPI.update(selected.id, {
          ...payload,
          is_active: clsForm.is_active,
        });
        toast.success("ತರಗತಿ ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ ✅");
      } else {
        await classAPI.create(payload);
        toast.success("ತರಗತಿ ಮಾಡಲಾಗಿದೆ ✅");
      }
      closeModal();
      load();
    } catch (e) {
      const msg = e.response?.data?.message || "";
      if (
        msg.includes("ನಿಷ್ಕ್ರಿಯ") ||
        msg.includes("Duplicate") ||
        msg.includes("ಈಗಾಗಲೇ")
      ) {
        // Fetch ALL classes (including inactive) fresh from server
        try {
          const { data } = await classAPI.list({ active_only: false });
          const all = data.data || [];
          setClasses(all);
          const num = parseInt(toEnglish(String(clsForm.class_number)));
          // Find the inactive class - check all possible ways is_active can be falsy
          const found = all.find(
            (c) =>
              parseInt(c.class_number) === num &&
              (c.is_active === 0 ||
                c.is_active === false ||
                c.is_active === "0"),
          );
          if (found) {
            setInlineReactivate(found);
            return; // don't show toast - green box will show instead
          }
        } catch (_) {}
        // Fallback if API fails or class not found
        toast(
          msg.replace(
            "'ತರಗತಿಗಳು' ಟ್ಯಾಬ್‌ನಲ್ಲಿ ♻️ ಒತ್ತಿ ಮತ್ತೆ ಸಕ್ರಿಯ ಮಾಡಿ.",
            "",
          ),
          { icon: "⚠️", duration: 5000 },
        );
        setShowInactive(true);
      } else {
        toast.error(msg || "ತಪ್ಪಾಗಿದೆ");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Deactivate class (soft delete) ──────────────────────
  const deactivateClass = async () => {
    setLoading(true);
    try {
      await classAPI.update(selected.id, {
        class_number: selected.class_number,
        name_kannada: selected.name_kannada,
        name_english: selected.name_english,
        display_order: selected.display_order,
        is_active: 0,
      });
      toast.success("ತರಗತಿ ನಿಷ್ಕ್ರಿಯ ಮಾಡಲಾಗಿದೆ — ಮತ್ತೆ ಸಕ್ರಿಯ ಮಾಡಬಹುದು");
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    } finally {
      setLoading(false);
    }
  };

  // ── Reactivate class ────────────────────────────────────
  const handleInlineReactivate = async () => {
    if (!inlineReactivate) return;
    try {
      // Reactivate AND update name from form (user may have typed correct name)
      const updatedData = {
        class_number: inlineReactivate.class_number,
        name_kannada:
          clsForm.name_kannada.trim() || inlineReactivate.name_kannada,
        name_english:
          clsForm.name_english.trim() || inlineReactivate.name_english,
        display_order:
          inlineReactivate.display_order || inlineReactivate.class_number,
        is_active: 1,
      };
      await classAPI.update(inlineReactivate.id, updatedData);
      toast.success(
        `✅ ತರಗತಿ ${inlineReactivate.class_number} ಮತ್ತೆ ಸಕ್ರಿಯ ಆಗಿದೆ!`,
      );
      setInlineReactivate(null);
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    }
  };

  const reactivateClass = async (cls) => {
    try {
      await classAPI.update(cls.id, { ...cls, is_active: 1 });
      toast.success(`${cls.name_kannada} ಮತ್ತೆ ಸಕ್ರಿಯ ಆಗಿದೆ ✅`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    }
  };

  // ── Save Group ──────────────────────────────────────────
  const saveGroup = async () => {
    if (!grpForm.name_kannada.trim()) {
      toast.error("ಕನ್ನಡ ಹೆಸರು ಅಗತ್ಯ");
      return;
    }
    setLoading(true);
    try {
      if (modal === "editGrp") {
        await groupAPI.update(selected.id, grpForm);
        toast.success("ಗುಂಪು ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ ✅");
      } else {
        await groupAPI.create(grpForm);
        toast.success("ಗುಂಪು ಮಾಡಲಾಗಿದೆ ✅");
      }
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    } finally {
      setLoading(false);
    }
  };

  // ── Delete Group ──────────────────────────────────────────
  const deleteGroup = async () => {
    setLoading(true);
    try {
      await groupAPI.update(selected.id, {
        name_kannada: selected.name_kannada,
        name_english: selected.name_english,
        description_kannada: selected.description_kannada || "",
        is_active: 0,
      });
      toast.success("ಗುಂಪು ನಿಷ್ಕ್ರಿಯ ಮಾಡಲಾಗಿದೆ");
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    } finally {
      setLoading(false);
    }
  };

  // ── Assign class to group ───────────────────────────────
  const saveAssign = async () => {
    if (!assignForm.class_id || !assignForm.group_id) {
      toast.error("ತರಗತಿ ಮತ್ತು ಗುಂಪು ಆಯ್ಕೆ ಮಾಡಿ");
      return;
    }
    setLoading(true);
    try {
      const res = await groupAPI.assignClass(assignForm);
      if (res.data?.data?.action === "already_assigned") {
        toast("ತರಗತಿ ಈಗಾಗಲೇ ಈ ಗುಂಪಿನಲ್ಲಿದೆ", { icon: "ℹ️" });
      } else {
        toast.success("ತರಗತಿ ನಿಯೋಜಿಸಲಾಗಿದೆ ✅");
      }
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    } finally {
      setLoading(false);
    }
  };

  const getClassOrder = (cls) => {
  const text =
    cls.class_name_kannada ||
    cls.name_kannada ||
    cls.class_name_english ||
    "";

  // extract first number from class name
  const match = text.match(/\d+/);

  if (match) {
    return parseInt(match[0], 10);
  }

  return 999;
};

const sortClasses = (arr) =>
  arr.sort((a, b) => {
    // direct class_number sorting
    const numA = Number(a.class_number || 0);
    const numB = Number(b.class_number || 0);

    if (numA !== numB) {
      return numA - numB;
    }

    // fallback
    return Number(a.id || 0) - Number(b.id || 0);
  });

const activeClasses = sortClasses(
  classes.filter((c) => c.is_active)
);

const inactiveClasses = sortClasses(
  classes.filter((c) => !c.is_active)
);
  // Get class name for assign modal header
  const assignClass = classes.find(
    (c) => String(c.id) === String(assignForm.class_id),
  );
  const currentGroupName = assignClass?.group_name_kannada;
  const newGroupName = groups.find(
    (g) => String(g.id) === String(assignForm.group_id),
  )?.name_kannada;

  return (
    <div className="common-page">
      <div className="page-header">
        <h1 className="page-title">🏫 ತರಗತಿ ಮತ್ತು ಗುಂಪು ನಿರ್ವಹಣೆ</h1>
        <p className="page-desc">ತರಗತಿ ಮಾಡಿ, ಸಂಪಾದಿಸಿ, ಗುಂಪಿಗೆ ನಿಯೋಜಿಸಿ</p>
      </div>

      {/* Inactive warning banner */}
      {inactiveClasses.length > 0 && (
        <div
          style={{
            background: "rgba(251,191,36,0.1)",
            border: "1px solid rgba(251,191,36,0.3)",
            borderRadius: "12px",
            padding: "0.8rem 1.2rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.8rem",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: "1.2rem" }}>⚠️</span>
          <span
            style={{
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.82rem",
              color: "#fde047",
              flex: 1,
            }}
          >
            {inactiveClasses.length} ತರಗತಿ(ಗಳು) ನಿಷ್ಕ್ರಿಯ — ಹಾಜರಾತಿ ಮತ್ತು
            ಲೆಕ್ಕದಲ್ಲಿ ಕಾಣಿಸುತ್ತಿಲ್ಲ
          </span>
          <button
            style={{
              background: "rgba(251,191,36,0.2)",
              border: "1px solid rgba(251,191,36,0.4)",
              color: "#fde047",
              borderRadius: "8px",
              padding: "0.3rem 0.8rem",
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? "▲ ಮರೆ ಮಾಡಿ" : "▼ ಮರು-ಸಕ್ರಿಯ ಮಾಡಲು ಇಲ್ಲಿ ಒತ್ತಿ"}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="crud-tabs">
        <button
          className={`crud-tab ${tab === "classes" ? "active" : ""}`}
          onClick={() => setTab("classes")}
        >
          🏫 ತರಗತಿಗಳು ({activeClasses.length})
        </button>
        <button
          className={`crud-tab ${tab === "groups" ? "active" : ""}`}
          onClick={() => setTab("groups")}
        >
          📁 ಗುಂಪುಗಳು ({groups.length})
        </button>
      </div>

      {/* ── CLASSES TAB ── */}
      {tab === "classes" && (
        <>
          <div className="action-bar">
            <button
              className="act-btn act-green"
              onClick={() => {
                setClsForm({
                  class_number: "",
                  name_kannada: "",
                  name_english: "",
                  display_order: "",
                });
                setModal("addCls");
              }}
            >
              + ಹೊಸ ತರಗತಿ
            </button>
          </div>

          {activeClasses.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "rgba(255,255,255,0.4)",
                fontFamily: "Noto Sans Kannada",
                fontSize: "0.88rem",
              }}
            >
              ಯಾವ ತರಗತಿಯೂ ಇಲ್ಲ. "+ ಹೊಸ ತರಗತಿ" ಒತ್ತಿ ಮಾಡಿ.
            </div>
          )}

          <div className="classes-grid">
            {activeClasses.map((cls) => (
              <div
                key={cls.id}
                className={`class-card ${cls.group_id ? "assigned" : "unassigned"}`}
              >
                <div className="cc-number">{cls.class_number}</div>
                <div className="cc-name">{cls.name_kannada}</div>
                <div className="cc-group">
                  {cls.group_name_kannada ? (
                    <span className="group-tag">{cls.group_name_kannada}</span>
                  ) : (
                    <span className="no-group-tag">⚠️ ಗುಂಪಿಲ್ಲ</span>
                  )}
                </div>
                <div className="cc-actions">
                  {/* Assign/Reassign button */}
                  <button
                    className="cc-btn"
                    style={{
                      background: "rgba(74,222,128,0.1)",
                      border: "1px solid rgba(74,222,128,0.25)",
                      fontSize: "0.65rem",
                      color: "#86efac",
                    }}
                    onClick={() => openAssign(cls)}
                    title={cls.group_id ? "ಗುಂಪು ಬದಲಿಸಿ" : "ಗುಂಪಿಗೆ ನಿಯೋಜಿಸಿ"}
                  >
                    {cls.group_id ? "🔄" : "🔗"}
                  </button>
                  <button
                    className="cc-btn cc-edit"
                    onClick={() => openEditCls(cls)}
                    title="ಸಂಪಾದಿಸಿ"
                  >
                    ✏️
                  </button>
                  <button
                    className="cc-btn cc-del"
                    onClick={() => {
                      setSelected(cls);
                      setModal("deleteCls");
                    }}
                    title="ನಿಷ್ಕ್ರಿಯ"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Inactive classes — shown as clear list with big restore button */}
          {showInactive && inactiveClasses.length > 0 && (
            <div
              style={{
                marginTop: "1rem",
                background: "rgba(251,191,36,0.06)",
                border: "1px solid rgba(251,191,36,0.2)",
                borderRadius: "14px",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  fontFamily: "Noto Sans Kannada",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  color: "#fde047",
                  marginBottom: "0.8rem",
                }}
              >
                ♻️ ನಿಷ್ಕ್ರಿಯ ತರಗತಿಗಳು — ಮತ್ತೆ ಸಕ್ರಿಯ ಮಾಡಲು "ಮರು-ಸಕ್ರಿಯ" ಬಟನ್
                ಒತ್ತಿ
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {inactiveClasses.map((cls) => (
                  <div
                    key={cls.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "rgba(255,255,255,0.04)",
                      borderRadius: "10px",
                      padding: "0.7rem 1rem",
                      gap: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.8rem",
                      }}
                    >
                      <span
                        style={{
                          background: "rgba(255,255,255,0.15)",
                          color: "rgba(255,255,255,0.5)",
                          borderRadius: "50%",
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "Syne",
                          fontWeight: 800,
                        }}
                      >
                        {cls.class_number}
                      </span>
                      <div>
                        <div
                          style={{
                            fontFamily: "Noto Sans Kannada",
                            fontSize: "0.85rem",
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          {cls.name_kannada}
                        </div>
                        <div
                          style={{
                            fontFamily: "Noto Sans Kannada",
                            fontSize: "0.68rem",
                            color: "#f87171",
                          }}
                        >
                          ನಿಷ್ಕ್ರಿಯ — ಹಾಜರಾತಿಯಲ್ಲಿ ತೋರಿಸುತ್ತಿಲ್ಲ
                        </div>
                      </div>
                    </div>
                    <button
                      style={{
                        background:
                          "linear-gradient(135deg,rgba(74,222,128,0.2),rgba(22,163,74,0.15))",
                        border: "1px solid rgba(74,222,128,0.4)",
                        color: "#4ade80",
                        borderRadius: "10px",
                        padding: "0.5rem 1.1rem",
                        cursor: "pointer",
                        fontFamily: "Noto Sans Kannada",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => reactivateClass(cls)}
                    >
                      ✅ ಮರು-ಸಕ್ರಿಯ ಮಾಡಿ
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── GROUPS TAB ── */}
      {tab === "groups" && (
        <>
          <div className="action-bar">
            <button
              className="act-btn act-green"
              onClick={() => {
                setGrpForm({
                  name_kannada: "",
                  name_english: "",
                  description_kannada: "",
                });
                setModal("addGrp");
              }}
            >
              + ಹೊಸ ಗುಂಪು
            </button>
          </div>
          <div className="section-grid">
            {groups.map((g) => (
              <div key={g.id} className="info-card">
                <div className="info-card-header">
                  <span className="card-icon">📁</span>
                  <div style={{ flex: 1 }}>
                    <div className="card-title-kn">{g.name_kannada}</div>
                    <div className="card-title-en">{g.name_english}</div>
                    {g.description_kannada && (
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "rgba(255,255,255,0.35)",
                          marginTop: "2px",
                        }}
                      >
                        {g.description_kannada}
                      </div>
                    )}
                  </div>
                  <span className="badge-count">
                    {g.classes?.length || 0} ತರಗತಿ
                  </span>
                </div>
                <div className="class-chips">
                  {(g.classes || [])
  .sort((a, b) => Number(a.class_number) - Number(b.class_number))
  .map((c) => (
    <span key={c.id} className="class-chip">
      {c.name_kannada}
    </span>
))}
                  {(!g.classes || g.classes.length === 0) && (
                    <span className="no-class-msg">ತರಗತಿ ನಿಯೋಜಿಸಿಲ್ಲ</span>
                  )}
                </div>
                <div className="info-card-actions">
                  <button
                    className="ic-btn ic-edit"
                    onClick={() => openEditGrp(g)}
                  >
                    ✏️ ಸಂಪಾದಿಸಿ
                  </button>
                  <button
                    className="ic-btn ic-del"
                    onClick={() => {
                      setSelected(g);
                      setModal("deleteGrp");
                    }}
                  >
                    🗑️ ಅಳಿಸಿ
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── MODALS ── */}

      {/* Add/Edit Class */}
      {(modal === "addCls" || modal === "editCls") && (
        <Modal
          title={modal === "editCls" ? "ತರಗತಿ ಸಂಪಾದಿಸಿ" : "ಹೊಸ ತರಗತಿ ಮಾಡಿ"}
          onClose={closeModal}
        >
          <FormRow label="ತರಗತಿ ಸಂಖ್ಯೆ *" hint="(1 or ೧)">
            <NumInput
              value={clsForm.class_number}
              onChange={(v) =>
                setClsForm((p) => ({
                  ...p,
                  class_number: v,
                  name_kannada: p.name_kannada || `${toKannada(v)}ನೇ ತರಗತಿ`,
                  name_english: p.name_english || `Class ${v}`,
                  display_order: p.display_order || v,
                }))
              }
              placeholder="1 ಅಥವಾ ೧"
            />
          </FormRow>
          <FormRow label="ಕನ್ನಡ ಹೆಸರು *">
            <input
              className="modal-input"
              placeholder="೧ನೇ ತರಗತಿ"
              value={clsForm.name_kannada}
              onChange={(e) =>
                setClsForm((p) => ({ ...p, name_kannada: e.target.value }))
              }
            />
          </FormRow>
          <FormRow label="English ಹೆಸರು">
            <input
              className="modal-input"
              placeholder="Class 1"
              value={clsForm.name_english}
              onChange={(e) =>
                setClsForm((p) => ({ ...p, name_english: e.target.value }))
              }
            />
          </FormRow>
          {modal === "editCls" && (
            <FormRow label="ಸ್ಥಿತಿ">
              <select
                className="modal-input"
                value={clsForm.is_active}
                onChange={(e) =>
                  setClsForm((p) => ({
                    ...p,
                    is_active: parseInt(e.target.value),
                  }))
                }
              >
                <option value={1}>✅ ಸಕ್ರಿಯ</option>
                <option value={0}>❌ ನಿಷ್ಕ್ರಿಯ</option>
              </select>
            </FormRow>
          )}

          {/* Inline reactivate prompt — shown when class already exists but is inactive */}
          {inlineReactivate && (
            <div
              style={{
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.3)",
                borderRadius: "12px",
                padding: "1rem",
                marginTop: "0.5rem",
              }}
            >
              <div
                style={{
                  fontFamily: "Noto Sans Kannada",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#4ade80",
                  marginBottom: "0.4rem",
                }}
              >
                ⚠️ ತರಗತಿ {inlineReactivate.class_number} ನಿಷ್ಕ್ರಿಯ ಸ್ಥಿತಿಯಲ್ಲಿ
                ಈಗಾಗಲೇ ಇದೆ
              </div>
              <div
                style={{
                  fontFamily: "Noto Sans Kannada",
                  fontSize: "0.75rem",
                  color: "rgba(255,255,255,0.55)",
                  marginBottom: "0.8rem",
                  lineHeight: 1.6,
                }}
              >
                ಹೊಸ ತರಗತಿ ಮಾಡಬೇಕಿಲ್ಲ. ಕೆಳಗಿನ ಬಟನ್ ಒತ್ತಿದರೆ ಮತ್ತೆ ಸಕ್ರಿಯ ಆಗುತ್ತದೆ
                ಮತ್ತು ನೀವು ನಮೂದಿಸಿದ ಹೆಸರು ಕೂಡ ಅಪ್‌ಡೇಟ್ ಆಗುತ್ತದೆ:
              </div>
              <div style={{ display: "flex", gap: "0.6rem" }}>
                <button
                  style={{
                    flex: 1,
                    padding: "0.65rem",
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg,rgba(74,222,128,0.25),rgba(22,163,74,0.2))",
                    border: "1px solid rgba(74,222,128,0.4)",
                    color: "#fff",
                    fontFamily: "Noto Sans Kannada",
                    fontSize: "0.88rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(74,222,128,0.2)",
                  }}
                  onClick={handleInlineReactivate}
                >
                  ✅ "{inlineReactivate.name_kannada}" ಮತ್ತೆ ಸಕ್ರಿಯ ಮಾಡಿ
                </button>
                <button
                  style={{
                    padding: "0.65rem 0.9rem",
                    borderRadius: "10px",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.5)",
                    fontFamily: "Noto Sans Kannada",
                    fontSize: "0.82rem",
                    cursor: "pointer",
                  }}
                  onClick={() => setInlineReactivate(null)}
                >
                  ರದ್ದು
                </button>
              </div>
            </div>
          )}

          {!inlineReactivate && (
            <ModalActions
              loading={loading}
              onSave={saveClass}
              onCancel={closeModal}
              saveLabel={modal === "editCls" ? "ಅಪ್‌ಡೇಟ್ ಮಾಡಿ" : "ಮಾಡಿ"}
            />
          )}
        </Modal>
      )}

      {/* Add/Edit Group */}
      {(modal === "addGrp" || modal === "editGrp") && (
        <Modal
          title={modal === "editGrp" ? "ಗುಂಪು ಸಂಪಾದಿಸಿ" : "ಹೊಸ ಗುಂಪು ಮಾಡಿ"}
          onClose={closeModal}
        >
          <FormRow label="ಕನ್ನಡ ಹೆಸರು *">
            <input
              className="modal-input"
              placeholder="ಕಿರಿಯ ಪ್ರಾಥಮಿಕ"
              value={grpForm.name_kannada}
              onChange={(e) =>
                setGrpForm((p) => ({ ...p, name_kannada: e.target.value }))
              }
            />
          </FormRow>
          <FormRow label="English ಹೆಸರು">
            <input
              className="modal-input"
              placeholder="Lower Primary"
              value={grpForm.name_english}
              onChange={(e) =>
                setGrpForm((p) => ({ ...p, name_english: e.target.value }))
              }
            />
          </FormRow>
          <FormRow label="ವಿವರಣೆ">
            <input
              className="modal-input"
              placeholder="೧ ರಿಂದ ೫ನೇ ತರಗತಿ..."
              value={grpForm.description_kannada}
              onChange={(e) =>
                setGrpForm((p) => ({
                  ...p,
                  description_kannada: e.target.value,
                }))
              }
            />
          </FormRow>
          <ModalActions
            loading={loading}
            onSave={saveGroup}
            onCancel={closeModal}
            saveLabel={modal === "editGrp" ? "ಅಪ್‌ಡೇಟ್ ಮಾಡಿ" : "ಮಾಡಿ"}
          />
        </Modal>
      )}

      {/* Assign Class to Group */}
      {modal === "assign" && (
        <Modal title="ತರಗತಿ ಗುಂಪಿಗೆ ನಿಯೋಜಿಸಿ" onClose={closeModal}>
          {/* Show current state */}
          {selected && (
            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                borderRadius: "10px",
                padding: "0.7rem 1rem",
                marginBottom: "0.5rem",
                fontFamily: "Noto Sans Kannada",
                fontSize: "0.8rem",
              }}
            >
              <span style={{ color: "rgba(255,255,255,0.5)" }}>ತರಗತಿ: </span>
              <strong style={{ color: "#fff" }}>{selected.name_kannada}</strong>
              {currentGroupName && (
                <>
                  <span style={{ color: "rgba(255,255,255,0.5)" }}>
                    {" "}
                    · ಈಗಿರುವ ಗುಂಪು:{" "}
                  </span>
                  <span style={{ color: "#fbbf24" }}>{currentGroupName}</span>
                </>
              )}
            </div>
          )}
          {/* Show what will change */}
          {newGroupName &&
            currentGroupName &&
            newGroupName !== currentGroupName && (
              <div
                style={{
                  background: "rgba(74,222,128,0.08)",
                  border: "1px solid rgba(74,222,128,0.2)",
                  borderRadius: "8px",
                  padding: "0.5rem 0.8rem",
                  marginBottom: "0.5rem",
                  fontFamily: "Noto Sans Kannada",
                  fontSize: "0.75rem",
                  color: "#86efac",
                }}
              >
                🔄 {currentGroupName} → {newGroupName} ಗೆ ಬದಲಾಗುತ್ತದೆ
              </div>
            )}
          {newGroupName && !currentGroupName && (
            <div
              style={{
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.2)",
                borderRadius: "8px",
                padding: "0.5rem 0.8rem",
                marginBottom: "0.5rem",
                fontFamily: "Noto Sans Kannada",
                fontSize: "0.75rem",
                color: "#86efac",
              }}
            >
              ✅ {newGroupName} ಗೆ ನಿಯೋಜಿಸಲಾಗುತ್ತದೆ
            </div>
          )}

          <FormRow label="ತರಗತಿ ಆಯ್ಕೆ *">
            <select
              className="modal-input"
              value={assignForm.class_id}
              onChange={(e) =>
                setAssignForm((p) => ({ ...p, class_id: e.target.value }))
              }
            >
              <option value="">— ತರಗತಿ ಆಯ್ಕೆ ಮಾಡಿ —</option>
              {activeClasses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name_kannada}
                  {c.group_name_kannada
                    ? ` (ಈಗ: ${c.group_name_kannada})`
                    : " ⚠️ ಗುಂಪಿಲ್ಲ"}
                </option>
              ))}
            </select>
          </FormRow>
          <FormRow label="ಗುಂಪು ಆಯ್ಕೆ *">
            <select
              className="modal-input"
              value={assignForm.group_id}
              onChange={(e) =>
                setAssignForm((p) => ({ ...p, group_id: e.target.value }))
              }
            >
              <option value="">— ಗುಂಪು ಆಯ್ಕೆ ಮಾಡಿ —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name_kannada}
                </option>
              ))}
            </select>
          </FormRow>
          <FormRow label="ಕಾರಣ (ಐಚ್ಛಿಕ)">
            <input
              className="modal-input"
              placeholder="ಸರ್ಕಾರ ನಿಯಮ ಬದಲಾವಣೆ..."
              value={assignForm.change_reason}
              onChange={(e) =>
                setAssignForm((p) => ({ ...p, change_reason: e.target.value }))
              }
            />
          </FormRow>
          <ModalActions
            loading={loading}
            onSave={saveAssign}
            onCancel={closeModal}
            saveLabel="✅ ನಿಯೋಜಿಸಿ"
          />
        </Modal>
      )}

      {/* Delete Group confirm */}
      {modal === "deleteGrp" && (
        <ConfirmModal
          title="ಗುಂಪು ನಿಷ್ಕ್ರಿಯ ಮಾಡಿ"
          message={`"${selected?.name_kannada}" ಗುಂಪು ನಿಷ್ಕ್ರಿಯ ಮಾಡುವಿರಾ? ಈ ಗುಂಪಿನ ಆಹಾರ ಸಂರಚನೆ ಕೆಲಸ ನಿಲ್ಲುತ್ತದೆ.`}
          onConfirm={deleteGroup}
          onCancel={closeModal}
          loading={loading}
        />
      )}

      {/* Deactivate Class confirm */}
      {modal === "deleteCls" && (
        <ConfirmModal
          title="ತರಗತಿ ನಿಷ್ಕ್ರಿಯ ಮಾಡಿ"
          message={`"${selected?.name_kannada}" ಅನ್ನು ನಿಷ್ಕ್ರಿಯ ಮಾಡುವಿರಾ?\n\nಹಳೆಯ ಹಾಜರಾತಿ ಮತ್ತು ಲೆಕ್ಕ ದಾಖಲೆಗಳು ಉಳಿಯುತ್ತವೆ. ನಂತರ ♻️ ಒತ್ತಿ ಮತ್ತೆ ಸಕ್ರಿಯ ಮಾಡಬಹುದು.`}
          onConfirm={deactivateClass}
          onCancel={closeModal}
          loading={loading}
        />
      )}

      <style>{`
        .crud-tabs{display:flex;gap:.5rem;margin-bottom:1.5rem;flex-wrap:wrap}
        .crud-tab{padding:.55rem 1.2rem;border-radius:10px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);color:rgba(255,255,255,.6);font-family:'Noto Sans Kannada',sans-serif;font-size:.82rem;cursor:pointer;transition:all .2s}
        .crud-tab.active{background:linear-gradient(135deg,rgba(22,163,74,.2),rgba(21,128,61,.1));border-color:rgba(74,222,128,.3);color:#86efac}
        .cc-actions{display:flex;gap:.3rem;margin-top:.5rem;justify-content:center;flex-wrap:wrap}
        .cc-btn{background:none;border:1px solid rgba(255,255,255,.1);border-radius:7px;padding:.2rem .45rem;cursor:pointer;font-size:.75rem;transition:all .2s}
        .cc-edit:hover{background:rgba(96,165,250,.15);border-color:rgba(96,165,250,.3)}
        .cc-del:hover{background:rgba(248,113,113,.15);border-color:rgba(248,113,113,.3)}
        .info-card-actions{display:flex;gap:.5rem;margin-top:.8rem;padding-top:.6rem;border-top:1px solid rgba(255,255,255,.06)}
        .ic-btn{flex:1;padding:.4rem .6rem;border-radius:8px;border:1px solid;font-family:'Noto Sans Kannada',sans-serif;font-size:.75rem;cursor:pointer;transition:all .2s}
        .ic-edit{background:rgba(96,165,250,.1);border-color:rgba(96,165,250,.25);color:#93c5fd}
        .ic-edit:hover{background:rgba(96,165,250,.2)}
      `}</style>
    </div>
  );
}
